/**
 * useOAuthPopup -- React hook for the OAuth 2.0 popup flow.
 *
 * Security features:
 * - Cryptographic state generation via Web Crypto API
 * - PKCE (Proof Key for Code Exchange) support
 * - sessionStorage persistence with 10-minute expiration
 * - Constant-time state comparison to prevent timing attacks
 * - Automatic cleanup on popup close or component unmount
 *
 * Flow:
 * 1. Calls GET /authorize to get the authorization URL and backend-generated state token
 * 2. Stores OAuthState in sessionStorage (keyed by backend state token)
 * 3. Opens a popup window to the authorization URL
 * 4. Listens for a postMessage from the callback page
 * 5. Validates state with constant-time comparison
 * 6. Calls POST /complete with the completion token (backend uses its own PKCE verifier)
 * 7. Returns the OAuth credentials for the wizard
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient } from '@/lib/api/client';
import { oauthDebug, oauthWarn } from '@/lib/debug/oauthDebug';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface OAuthResult {
  success: boolean;
  portal_id?: string;
  scopes_granted?: string[];
  credentials?: Record<string, any>;
  message?: string;
  email?: string;
  session_token?: string;
}

interface UseOAuthPopupReturn {
  startOAuth: () => Promise<void>;
  isLoading: boolean;
  error: string | null;
  isConnected: boolean;
  result: OAuthResult | null;
  reset: () => void;
}

interface PostMessageData {
  type: string;
  success: boolean;
  completion_token?: string;
  nonce?: string;
  portal_id?: string;
  error?: string;
  state?: string;
}

/**
 * Persisted OAuth state stored in sessionStorage during the popup flow.
 */
export interface OAuthState {
  /** Cryptographic random state parameter */
  state: string;
  /** PKCE code verifier (sent to token endpoint after callback) -- optional, backend manages its own */
  codeVerifier?: string;
  /** Which connector type initiated the flow */
  connectorType: string;
  /** Unix timestamp (ms) when state was created */
  timestamp: number;
  /** Unix timestamp (ms) when this state expires (timestamp + 10 minutes) */
  expiresAt: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const POPUP_WIDTH = 600;
const POPUP_HEIGHT = 700;
const POPUP_POLL_INTERVAL = 500;
const STATE_TTL_MS = 10 * 60 * 1000; // 10 minutes
const SESSION_STORAGE_PREFIX = 'oauth_state_';
/** Timeout (ms) after which the poll timer treats an in-flight /complete as stalled. */
const COMPLETING_TIMEOUT_MS = 30_000;

// ---------------------------------------------------------------------------
// Cryptographic helpers (Web Crypto API -- no external dependencies)
// ---------------------------------------------------------------------------

/**
 * Generate a cryptographically secure random state string (64-char hex).
 * Uses crypto.getRandomValues for CSPRNG.
 */
export function generateCryptographicState(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Characters allowed in a PKCE code_verifier per RFC 7636 Section 4.1:
 * unreserved = ALPHA / DIGIT / "-" / "." / "_" / "~"
 */
const PKCE_CHARSET =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';

/**
 * Generate a PKCE code_verifier and code_challenge pair.
 *
 * - code_verifier: 128 characters sampled uniformly from the unreserved set
 * - code_challenge: BASE64URL( SHA-256( code_verifier ) )
 */
export async function generatePKCE(): Promise<{
  codeVerifier: string;
  codeChallenge: string;
}> {
  // Build the code_verifier from random bytes
  const randomBytes = new Uint8Array(128);
  crypto.getRandomValues(randomBytes);
  let codeVerifier = '';
  for (let i = 0; i < 128; i++) {
    codeVerifier += PKCE_CHARSET[randomBytes[i] % PKCE_CHARSET.length];
  }

  // Compute SHA-256 hash of the verifier
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digest = await crypto.subtle.digest('SHA-256', data);

  // Base64url-encode the digest (no padding)
  const hashArray = new Uint8Array(digest);
  let base64 = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < hashArray.length; i += chunkSize) {
    base64 += String.fromCharCode(...hashArray.subarray(i, i + chunkSize));
  }
  const codeChallenge = btoa(base64)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

  return { codeVerifier, codeChallenge };
}

/**
 * Constant-time string comparison to prevent timing side-channel attacks.
 * Returns true only when both strings are equal and non-empty.
 */
export function constantTimeCompare(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let result = 0;
  for (let i = 0; i < a.length; i++) {
    result |= a.charCodeAt(i) ^ b.charCodeAt(i);
  }
  return result === 0;
}

// ---------------------------------------------------------------------------
// sessionStorage helpers
// ---------------------------------------------------------------------------

/**
 * Persist an OAuthState object into sessionStorage.
 */
export function storeOAuthState(oauthState: OAuthState): void {
  const key = `${SESSION_STORAGE_PREFIX}${oauthState.state}`;
  sessionStorage.setItem(key, JSON.stringify(oauthState));
}

/**
 * Retrieve and validate an OAuthState from sessionStorage.
 * Returns null if not found or expired (and removes expired entries).
 */
export function retrieveOAuthState(state: string): OAuthState | null {
  const key = `${SESSION_STORAGE_PREFIX}${state}`;
  const raw = sessionStorage.getItem(key);
  if (!raw) return null;

  try {
    const parsed: OAuthState = JSON.parse(raw);

    // Check expiration
    if (Date.now() > parsed.expiresAt) {
      sessionStorage.removeItem(key);
      return null;
    }

    return parsed;
  } catch {
    sessionStorage.removeItem(key);
    return null;
  }
}

/**
 * Remove an OAuthState entry from sessionStorage.
 */
export function clearOAuthState(state: string): void {
  const key = `${SESSION_STORAGE_PREFIX}${state}`;
  sessionStorage.removeItem(key);
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface OAuthEndpoints {
  provider: string;
  authorize_endpoint: string;
  complete_endpoint: string;
}

const DEFAULT_ENDPOINTS: OAuthEndpoints = {
  provider: 'hubspot',
  authorize_endpoint: '/connectors/hubspot/oauth/authorize',
  complete_endpoint: '/connectors/hubspot/oauth/complete',
};

export interface UseOAuthPopupOptions {
  /**
   * Connector instance name (e.g. "my-meta"). When provided, the hook forwards
   * it to `/authorize` as the `connector_name` query parameter. The backend
   * then takes the reauth-persist branch in `/complete` — tokens land in the
   * connector's `credentials_encrypted` column atomically, and `/complete`
   * omits credentials from the response body. See GitLab issue #183.
   *
   * Leave unset on the first-time wizard path; the backend then returns
   * credentials in the response and the wizard form captures them so the
   * create-connector mutation can use them.
   */
  connectorName?: string;
}

export function useOAuthPopup(
  providerOrConfig: string | OAuthEndpoints = 'hubspot',
  options?: UseOAuthPopupOptions,
): UseOAuthPopupReturn {
  // Resolve config: if a string is passed, use default HubSpot endpoints for backward compat
  const endpoints: OAuthEndpoints = typeof providerOrConfig === 'string'
    ? { ...DEFAULT_ENDPOINTS, provider: providerOrConfig }
    : providerOrConfig;
  const provider = endpoints.provider;
  const connectorName = options?.connectorName;
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [result, setResult] = useState<OAuthResult | null>(null);

  // Refs for cleanup
  const popupRef = useRef<Window | null>(null);
  const pollTimerRef = useRef<number | null>(null);
  const nonceRef = useRef<string | null>(null);
  const oauthStateRef = useRef<string | null>(null);

  /** Guards the poll timer while POST /complete is in-flight. */
  const completingRef = useRef<boolean>(false);
  /** Timestamp when completingRef was set to true, for timeout guard. */
  const completeStartedAtRef = useRef<number>(0);
  /** Origin of the OAuth callback URL (e.g. ngrok tunnel), extracted from the authorization URL's redirect_uri. */
  const callbackOriginRef = useRef<string | null>(null);

  /** True once the no-secret callback-reached beacon (#625) has been seen. */
  const callbackReachedRef = useRef<boolean>(false);
  /** True once a genuine result message (success or failure) has been handled. */
  const messageProcessedRef = useRef<boolean>(false);
  /** Mirrors isConnected for use inside the poll-timer closure (avoids stale capture). */
  const connectedRef = useRef<boolean>(false);

  // Cleanup popup, timers, and sessionStorage state
  const cleanup = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }
    popupRef.current = null;
    nonceRef.current = null;
    completingRef.current = false;
    completeStartedAtRef.current = 0;
    callbackReachedRef.current = false;
    messageProcessedRef.current = false;
    connectedRef.current = false;

    // Clean up sessionStorage state
    if (oauthStateRef.current) {
      clearOAuthState(oauthStateRef.current);
      oauthStateRef.current = null;
    }
  }, []);

  // Reset state
  const reset = useCallback(() => {
    cleanup();
    setIsLoading(false);
    setError(null);
    setIsConnected(false);
    setResult(null);
  }, [cleanup]);

  // Handle postMessage from callback popup
  const handleMessage = useCallback(
    async (event: MessageEvent) => {
      const data = event.data as PostMessageData;
      oauthDebug('Received postMessage:', { type: data?.type, origin: event.origin, data });
      if (!data) return;

      // Compute allowed origins once (app origin, derived API origin, callback origin).
      const expectedOrigins = new Set([window.location.origin]);
      try {
        const apiBase = `${window.location.protocol}//${window.location.hostname}:8000`;
        expectedOrigins.add(new URL(apiBase).origin);
      } catch { /* ignore malformed URL */ }
      if (callbackOriginRef.current) {
        expectedOrigins.add(callbackOriginRef.current);
      }

      // #625: no-secret "callback reached" beacon. Records that the callback page
      // ran even when the real (origin-restricted) result message is dropped.
      if (data.type === 'oauth_callback_reached') {
        if (expectedOrigins.has(event.origin)) {
          callbackReachedRef.current = true;
          oauthDebug('Callback-reached beacon recorded');
        }
        return;
      }

      // Only handle our OAuth callback messages (provider-specific or generic)
      const expectedType = `${provider}_oauth_callback`;
      if (data.type !== expectedType && data.type !== 'oauth_callback') return;
      oauthDebug(`Processing ${data.type}`);

      if (!expectedOrigins.has(event.origin)) {
        oauthWarn('postMessage from unexpected origin:', event.origin, 'expected:', [...expectedOrigins]);
        return;
      }

      // #625: a genuine result message was received (not dropped) — even if we reject it below.
      messageProcessedRef.current = true;

      // Validate state parameter -- mandatory on success callbacks
      if (data.success) {
        if (!data.state) {
          oauthWarn('Missing state parameter in success callback');
          setError('OAuth state validation failed. Please try again.');
          setIsLoading(false);
          return;
        }

        if (!oauthStateRef.current) {
          oauthWarn('Missing local OAuth state reference');
          setError('OAuth state validation failed. Please try again.');
          setIsLoading(false);
          return;
        }

        if (!constantTimeCompare(data.state, oauthStateRef.current)) {
          oauthWarn('State mismatch -- possible CSRF attack');
          setError('OAuth state validation failed. Please try again.');
          setIsLoading(false);
          return;
        }

        // Verify the state hasn't expired in sessionStorage
        const storedState = retrieveOAuthState(data.state);
        if (!storedState) {
          oauthWarn('OAuth state expired or not found');
          setError('OAuth session expired. Please try again.');
          setIsLoading(false);
          return;
        }
      }

      // Duplicate-callback guard: reject re-entrant calls while /complete is in-flight
      if (completingRef.current) {
        oauthDebug('Ignoring duplicate postMessage while /complete is in-flight');
        return;
      }

      // Failure path: close popup immediately and bail out
      if (!data.success) {
        oauthDebug('OAuth failed:', data.error);
        if (popupRef.current && !popupRef.current.closed) {
          popupRef.current.close();
        }
        setError(data.error || 'Authorization was denied or failed');
        setIsLoading(false);
        if (oauthStateRef.current) {
          clearOAuthState(oauthStateRef.current);
          oauthStateRef.current = null;
        }
        return;
      }

      // Missing token/nonce path: close popup immediately and bail out
      if (!data.completion_token || !data.nonce) {
        oauthDebug('Missing completion_token or nonce');
        if (popupRef.current && !popupRef.current.closed) {
          popupRef.current.close();
        }
        setError('Invalid callback response -- missing token or nonce');
        setIsLoading(false);
        if (oauthStateRef.current) {
          clearOAuthState(oauthStateRef.current);
          oauthStateRef.current = null;
        }
        return;
      }

      // Call POST /complete to consume the completion token.
      // The backend uses its own PKCE code_verifier from the Redis session.
      // Set completingRef to gate the poll timer during the in-flight request.
      oauthDebug('Calling POST /complete with token');
      completeStartedAtRef.current = Date.now();
      completingRef.current = true;
      try {
        const response = await apiClient.post(
          endpoints.complete_endpoint,
          {
            completion_token: data.completion_token,
            nonce: data.nonce,
          }
        );

        oauthDebug('POST /complete response:', response.data);
        const completeResult: OAuthResult = response.data;
        if (completeResult.success) {
          oauthDebug('OAuth completed successfully, setting result');
          setResult(completeResult);
          setIsConnected(true);
          connectedRef.current = true;
          setError(null);
        } else {
          oauthDebug('OAuth complete returned failure:', completeResult.message);
          setError(completeResult.message || 'Failed to complete OAuth flow');
        }
      } catch (err: any) {
        oauthWarn('POST /complete error:', err);
        const msg =
          err.response?.data?.detail ||
          err.message ||
          'Failed to complete OAuth authorization';
        setError(msg);
      } finally {
        oauthDebug('Setting isLoading to false');
        setIsLoading(false);

        // Close popup after state updates so React batches them in the same cycle
        if (popupRef.current && !popupRef.current.closed) {
          oauthDebug('Closing popup window');
          popupRef.current.close();
        }

        // Clean up sessionStorage state after completion
        if (oauthStateRef.current) {
          clearOAuthState(oauthStateRef.current);
          oauthStateRef.current = null;
        }

        completingRef.current = false;
        completeStartedAtRef.current = 0;
      }
    },
    []
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
      window.removeEventListener('message', handleMessage);
    };
  }, [cleanup, handleMessage]);

  // Start the OAuth flow
  const startOAuth = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // 1. Get authorization URL from backend (backend generates state + PKCE).
      // When this hook was constructed with a connectorName (i.e. the user
      // clicked Reconnect on an existing connector — #183), forward it so the
      // backend can persist tokens atomically on /complete instead of relying
      // on the frontend to fold them into a settings PATCH.
      const response = connectorName
        ? await apiClient.get(endpoints.authorize_endpoint, {
            params: { connector_name: connectorName },
          })
        : await apiClient.get(endpoints.authorize_endpoint);
      const { authorization_url, nonce, state_token: state } = response.data;

      // 1b. Extract the callback origin from the redirect_uri in the authorization URL
      // so we accept postMessages from tunnels (e.g. ngrok) or external API domains.
      try {
        const authUrl = new URL(authorization_url);
        const redirectUri = authUrl.searchParams.get('redirect_uri');
        if (redirectUri) {
          callbackOriginRef.current = new URL(redirectUri).origin;
        }
      } catch { /* ignore parse errors */ }

      // 2. Persist the backend's state token in sessionStorage so handleMessage can validate it
      const now = Date.now();
      const oauthState: OAuthState = {
        state,
        connectorType: provider,
        timestamp: now,
        expiresAt: now + STATE_TTL_MS,
      };
      storeOAuthState(oauthState);
      oauthStateRef.current = state;
      nonceRef.current = nonce;

      // 3. Open popup window
      const left = window.screenX + (window.outerWidth - POPUP_WIDTH) / 2;
      const top = window.screenY + (window.outerHeight - POPUP_HEIGHT) / 2;
      const features = [
        `width=${POPUP_WIDTH}`,
        `height=${POPUP_HEIGHT}`,
        `left=${left}`,
        `top=${top}`,
        'toolbar=no',
        'menubar=no',
        'scrollbars=yes',
        'resizable=yes',
      ].join(',');

      const popup = window.open(authorization_url, `${provider}_oauth`, features);
      if (!popup) {
        setError('Popup was blocked. Please allow popups for this site and try again.');
        setIsLoading(false);
        clearOAuthState(state);
        oauthStateRef.current = null;
        return;
      }

      popupRef.current = popup;

      // 4. Listen for postMessage from the callback page
      window.addEventListener('message', handleMessage);

      // 5. Poll to detect if popup was closed without completing
      pollTimerRef.current = window.setInterval(() => {
        // Skip cleanup while POST /complete is in-flight
        if (completingRef.current) {
          // Timeout guard: if /complete has been in-flight for > 30 s, fall through to cleanup
          const elapsed = Date.now() - completeStartedAtRef.current;
          if (elapsed < COMPLETING_TIMEOUT_MS) {
            return;
          }
          oauthWarn('POST /complete timed out after 30 s — cleaning up');
          completingRef.current = false;
          completeStartedAtRef.current = 0;
        }

        if (popupRef.current && popupRef.current.closed) {
          clearInterval(pollTimerRef.current!);
          pollTimerRef.current = null;

          // Clean up sessionStorage on popup close without callback
          if (oauthStateRef.current) {
            clearOAuthState(oauthStateRef.current);
            oauthStateRef.current = null;
          }

          if (!connectedRef.current) {
            setIsLoading(false);
            if (!messageProcessedRef.current && callbackReachedRef.current) {
              // #625: the callback ran (beacon seen) but its result never arrived
              // — the OAuth callback origin is likely misconfigured.
              setError(
                "We didn't receive the authorization result. The OAuth callback " +
                  'origin may be misconfigured — contact your administrator, or try again.',
              );
            }
            // else: no beacon and no result → user closed the popup → stay quiet.
          }
        }
      }, POPUP_POLL_INTERVAL);
    } catch (err: any) {
      const msg =
        err.response?.data?.detail ||
        err.message ||
        'Failed to start OAuth flow';
      setError(msg);
      setIsLoading(false);

      // Clean up state on error
      if (oauthStateRef.current) {
        clearOAuthState(oauthStateRef.current);
        oauthStateRef.current = null;
      }
    }

  }, [handleMessage, provider, connectorName, endpoints.authorize_endpoint]);

  return {
    startOAuth,
    isLoading,
    error,
    isConnected,
    result,
    reset,
  };
}
